// Výběr trenéra pro ownera, sedí přímo v horní liště aplikace.
//
// 4.9.2026: dřív to byl bílý text na tmavém pruhu uvnitř trenérského portálu.
// Pruh je pryč, takže tlačítko je teď ve světlé sadě: fotka, jméno, pod ním
// "Náhled" a název stránky, vpravo šipka. Když není nikdo vybraný, tlačítko
// je zvýrazněné barvou značky, aby bylo jasné, co udělat.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Check, X } from "lucide-react";
import { useApiData } from "../lib/api.js";
import { useNahledTrenera } from "../lib/nahledTrenera.js";

// Google Drive sharing URL nelze použít přímo jako <img src>. Transformuje na
// thumbnail URL, která vrací skutečný obrázek a podporuje resize parametr.
export function transformPhotoUrl(url, sizePx = 200) {
  if (!url) return "";
  const s = String(url).trim();
  if (!s.includes("drive.google.com")) return s;
  const m1 = s.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  const m2 = s.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  const id = m1?.[1] || m2?.[1];
  if (!id) return s;
  return `https://drive.google.com/thumbnail?id=${id}&sz=w${sizePx}`;
}

/** Fotka trenéra, nebo iniciálky v barevném kruhu. */
export function Avatar({ photo, name, size = "md" }) {
  const sizeMap = {
    sm: "w-7 h-7 text-[11px]",
    md: "w-9 h-9 text-xs",
    lg: "w-11 h-11 sm:w-12 sm:h-12 text-sm",
  };
  const cls = sizeMap[size] || sizeMap.md;
  const [imgErr, setImgErr] = useState(false);
  const showImg = photo && !imgErr;
  const sizePx = size === "lg" ? 200 : 100;
  const finalUrl = showImg ? transformPhotoUrl(photo, sizePx) : "";

  if (showImg) {
    return (
      <img
        src={finalUrl}
        alt={name || ""}
        className={`${cls} rounded-full object-cover ring-2 ring-white/20 demo-photo flex-shrink-0`}
        onError={() => setImgErr(true)}
        referrerPolicy="no-referrer"
      />
    );
  }
  const initials = String(name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "").join("") || "?";
  const colors = [
    "bg-amber-500", "bg-rose-500", "bg-emerald-500", "bg-sky-500",
    "bg-brand-500", "bg-fuchsia-500", "bg-plum-500", "bg-teal-500",
  ];
  const hash = String(name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const color = colors[hash % colors.length];
  return (
    <div className={`${cls} rounded-full ${color} text-white font-bold flex items-center justify-center ring-2 ring-white/20 flex-shrink-0`}>
      {initials}
    </div>
  );
}

export default function VyberTrenera({ podtitulek = "" }) {
  const [vybrany, nastav] = useNahledTrenera();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const { data: trainersData } = useApiData(`/api/trainers?year=${new Date().getFullYear()}`);
  const trenéři = useMemo(
    () => (trainersData?.trainers || []).filter((t) => t.status === "Aktivní"),
    [trainersData]
  );
  const vybranyObj = useMemo(
    () => trenéři.find((t) => t.jmeno?.toLowerCase() === String(vybrany).toLowerCase()) || null,
    [trenéři, vybrany]
  );

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return trenéři;
    const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const q = norm(search);
    return trenéři.filter((t) => norm(t.jmeno).includes(q) || norm(t.lokalita).includes(q));
  }, [trenéři, search]);

  const prazdno = !vybrany;

  return (
    <div className="relative min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Vybrat, za kterého trenéra se díváš"
        className={`flex min-h-[52px] w-full min-w-0 items-center gap-2 rounded-xl border px-2 py-1.5 text-left transition-colors ${
          prazdno
            ? "border-2 border-brand bg-brand-50 hover:bg-brand-100"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        {vybrany
          ? <Avatar photo={vybranyObj?.foto} name={vybrany} size="md" />
          : <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-200 text-sm font-bold text-brand-deep">?</span>}
        <span className="min-w-0 flex-1">
          <span className={`block truncate text-[15px] font-bold leading-tight demo-sensitive ${prazdno ? "text-brand-deep" : "text-slate-900"}`}>
            {vybrany || "Vyber trenéra"}
          </span>
          <span className={`block truncate text-xs ${prazdno ? "text-brand" : "text-slate-500"}`}>
            {vybrany && podtitulek ? `Náhled, ${podtitulek}` : "Náhled za trenéra"}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${prazdno ? "text-brand" : "text-slate-400"} ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-40 mt-2 w-[min(22rem,92vw)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-100 bg-slate-50/50 p-2.5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Hledat jméno nebo lokalitu…"
                  className="field w-full pl-9 pr-8"
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 hover:bg-slate-100">
                    <X className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                )}
              </div>
              <div className="mt-1.5 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                {filtered.length} {filtered.length === 1 ? "trenér" : filtered.length < 5 ? "trenéři" : "trenérů"}
              </div>
            </div>

            <div className="max-h-[55vh] overflow-y-auto py-1">
              {trenéři.length === 0 && (
                <div className="px-4 py-6 text-center text-sm italic text-slate-400">Načítám…</div>
              )}
              {trenéři.length > 0 && filtered.length === 0 && (
                <div className="px-4 py-6 text-center text-sm italic text-slate-400">Nikdo nenalezen.</div>
              )}
              {filtered.map((t) => {
                const isCurrent = String(vybrany).toLowerCase() === t.jmeno?.toLowerCase();
                return (
                  <button
                    key={t.jmeno}
                    type="button"
                    onClick={() => { nastav(t.jmeno); setOpen(false); }}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      isCurrent ? "bg-brand-50 hover:bg-brand-100" : "hover:bg-slate-50 active:bg-slate-100"
                    }`}
                  >
                    <Avatar photo={t.foto} name={t.jmeno} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className={`truncate text-sm font-semibold ${isCurrent ? "text-brand-800" : "text-slate-800"}`}>{t.jmeno}</div>
                      <div className="truncate text-[11px] text-slate-500">
                        {t.lokalita || "-"}
                        {t.activeClientsCount > 0 && <span>, {t.activeClientsCount} klientů</span>}
                      </div>
                    </div>
                    {isCurrent && <Check className="h-4 w-4 flex-shrink-0 text-brand-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
